import React, { useEffect, useRef, useState } from 'react';

type TestTonePanelProps = {
  devices: MediaDeviceInfo[];
  selectedDeviceId: string;
  onDeviceChange: (deviceId: string) => void;
};

// Generates a simple sine-wave test tone and routes it to an <audio> element so we can apply setSinkId.
const TestTonePanel: React.FC<TestTonePanelProps> = ({ devices, selectedDeviceId, onDeviceChange }) => {
  const [freq, setFreq] = useState<number>(440);
  const [volume, setVolume] = useState<number>(60);
  const [playing, setPlaying] = useState<boolean>(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const oscRef = useRef<OscillatorNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const destRef = useRef<MediaStreamAudioDestinationNode | null>(null);

  const ensureContext = () => {
    if (!ctxRef.current) {
      ctxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return ctxRef.current!;
  };

  const applySink = async () => {
    const el = audioRef.current as any;
    if (!el) return;
    try {
      if (el.setSinkId && selectedDeviceId) {
        await el.setSinkId(selectedDeviceId);
      }
    } catch (e) {
      console.warn('setSinkId failed (falling back to default device):', e);
    }
  };

  const startTone = async () => {
    try {
      const ctx = ensureContext();
      if (ctx.state === 'suspended') await ctx.resume();

      // create graph: Oscillator -> Gain -> MediaStreamDestination -> <audio>.srcObject
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const dest = ctx.createMediaStreamDestination();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.value = volume / 100;
      osc.connect(gain);
      gain.connect(dest);
      osc.start();

      oscRef.current = osc;
      gainRef.current = gain;
      destRef.current = dest;

      const el = audioRef.current;
      if (el) {
        el.srcObject = dest.stream;
        await applySink();
        el.muted = false;
        await el.play().catch(() => {});
      }
      setPlaying(true);
    } catch (e) {
      console.error('Failed to start test tone:', e);
      alert('Failed to start test tone (browser permissions or device restrictions).');
    }
  };

  const stopTone = () => {
    try {
      oscRef.current?.stop();
      oscRef.current?.disconnect();
      gainRef.current?.disconnect();
      destRef.current = null;
      const el = audioRef.current;
      if (el) {
        el.pause();
        el.srcObject = null;
      }
    } catch {}
    setPlaying(false);
  };

  // Update frequency live
  useEffect(() => {
    if (oscRef.current) {
      try { oscRef.current.frequency.value = freq; } catch {}
    }
  }, [freq]);

  // Update volume live
  useEffect(() => {
    if (gainRef.current) {
      try { gainRef.current.gain.value = volume / 100; } catch {}
    }
  }, [volume]);

  // Apply sink changes when device changes while playing
  useEffect(() => {
    if (playing) {
      void applySink();
    }
  }, [selectedDeviceId, playing]);

  useEffect(() => () => { stopTone(); ctxRef.current?.close().catch(() => {}); }, []);

  return (
    <div className="bg-gray-700/70 border border-gray-600 rounded-lg p-5 space-y-4 mt-4">
      <h3 className="text-lg font-semibold text-white">Audio Routing Help & Test Tone</h3>
      <div className="text-sm text-gray-200 space-y-2">
        <p>
          The YouTube player runs in a browser window and cannot be routed by the app. Use your operating system&apos;s audio routing to pick the output device for the external display window.
        </p>
        <ul className="list-disc list-inside space-y-1">
          <li>
            <span className="font-semibold">Windows:</span> Settings → System → Sound → Volume mixer → select your browser playing the display window → set Output device (Bluetooth, USB interface, headphones).
          </li>
          <li>
            <span className="font-semibold">macOS:</span> System Settings → Sound. For per‑app routing, use tools like Loopback, BlackHole, or SoundSource.
          </li>
        </ul>
      </div>

      <div className="mt-3 p-3 bg-black/30 rounded-lg border border-gray-600">
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm text-gray-200 flex items-center gap-2">
            Output Device:
            <select
              className="bg-gray-800 text-white border border-gray-600 rounded px-2 py-1"
              value={selectedDeviceId}
              onChange={(e) => onDeviceChange(e.target.value)}
            >
              <option value="">Default</option>
              {devices.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>{d.label || 'Audio Device'}</option>
              ))}
            </select>
          </label>

          <label className="text-sm text-gray-200 flex items-center gap-2 ml-2">
            Frequency:
            <input type="range" min={100} max={2000} value={freq} onChange={(e) => setFreq(Number(e.target.value))} />
            <span className="w-14 text-right">{freq} Hz</span>
          </label>

          <label className="text-sm text-gray-200 flex items-center gap-2 ml-2">
            Volume:
            <input type="range" min={0} max={100} value={volume} onChange={(e) => setVolume(Number(e.target.value))} />
            <span className="w-10 text-right">{volume}</span>
          </label>

          {!playing ? (
            <button onClick={startTone} className="ml-auto bg-emerald-600 hover:bg-emerald-700 px-3 py-2 rounded text-white">
              ▶ Play Test Tone
            </button>
          ) : (
            <button onClick={stopTone} className="ml-auto bg-red-600 hover:bg-red-700 px-3 py-2 rounded text-white">
              ■ Stop
            </button>
          )}
        </div>
        <audio ref={audioRef} style={{ display: 'none' }} />
        <p className="text-[11px] text-gray-400 mt-2">
          Note: Browser may ask for permission and requires a user gesture. Test tone routes to the selected Output Device only; the YouTube player routes via OS volume mixer.
        </p>
      </div>
    </div>
  );
};

export default TestTonePanel;
